import React, { useState, useEffect } from 'react';
import { Brain, Clock, Hash, Loader, DollarSign, Zap } from 'lucide-react';

const DrillProgress = ({ isGenerating, startTime, tokensUsed, estimatedTokens, costIncurred, stage, message }) => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);

  useEffect(() => {
    let interval;
    if (isGenerating && startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimeElapsed(elapsed);
        
        // Estimate progress based on time (typical API response is 5-15 seconds)
        const estimatedTotalTime = 12; // seconds
        const progress = Math.min((elapsed / estimatedTotalTime) * 100, 95);
        setProgressPercentage(progress);
      }, 1000);
    } else {
      setTimeElapsed(0);
      setProgressPercentage(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating, startTime]);

  if (!isGenerating) {
    return null;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTokens = (tokens) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  const formatCost = (cost) => {
    if (cost === 0 || cost === null || cost === undefined) {
      return '計算中...';
    }
    return `¥${cost.toFixed(4)}`;
  };

  const getStageProgress = () => {
    const stages = {
      'preparing': 10,
      'analyzing': 25,
      'generating': 50,
      'processing': 75,
      'validating': 90,
      'complete': 100
    };
    return stages[stage] || progressPercentage;
  };

  const getStageColor = () => {
    const colors = {
      'preparing': 'bg-blue-500',
      'analyzing': 'bg-green-500',
      'generating': 'bg-yellow-500',
      'processing': 'bg-orange-500',
      'validating': 'bg-purple-500',
      'complete': 'bg-green-600'
    };
    return colors[stage] || 'bg-blue-500';
  };

  const getStageMessage = () => {
    const messages = {
      'preparing': '準備生成小學練習題...',
      'analyzing': '分析原始題目結構...',
      'generating': '正在生成 5 道小學練習題...',
      'processing': '處理 AI 回應...',
      'validating': '驗證題目完整性...',
      'complete': '小學練習題生成完成！'
    };
    return message || messages[stage] || '正在處理中...';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4 border border-gray-100">
        <div className="text-center">
          {/* Header */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="relative">
              <div className={`w-12 h-12 rounded-full ${getStageColor()} flex items-center justify-center animate-pulse`}>
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1">
                <Loader className="w-5 h-5 text-blue-600 animate-spin" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">AI 練習題生成中</h3>
              <p className="text-sm text-gray-600 mt-1">{getStageMessage()}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="w-full bg-gray-200 rounded-full h-4 mb-3">
              <div 
                className={`${getStageColor()} h-4 rounded-full transition-all duration-500 ease-out shadow-lg`}
                style={{ width: `${getStageProgress()}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>開始生成</span>
              <span className="font-semibold">{getStageProgress().toFixed(0)}%</span>
              <span>完成</span>
            </div>
          </div>

          {/* Three Key Metrics */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            {/* Time Elapsed */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-800">已用時間</span>
                  <div className="text-xs text-blue-600">Time Elapsed</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-blue-600">{formatTime(timeElapsed)}</span>
                <div className="text-xs text-blue-500">秒</div>
              </div>
            </div>

            {/* Tokens Used */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <Hash className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-800">已用 Token</span>
                  <div className="text-xs text-purple-600">Tokens Used</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-purple-600">
                  {tokensUsed ? formatTokens(tokensUsed) : '計算中...'}
                </span>
                {estimatedTokens && (
                  <div className="text-xs text-purple-500">
                    / {formatTokens(estimatedTokens)} 預估
                  </div>
                )}
              </div>
            </div>

            {/* Cost Incurred */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-green-800">成本費用</span>
                  <div className="text-xs text-green-600">Cost Incurred</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-green-600">{formatCost(costIncurred)}</span>
                <div className="text-xs text-green-500">人民幣</div>
              </div>
            </div>
          </div>

          {/* Processing Steps */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center">
              <Zap className="w-4 h-4 mr-2 text-yellow-500" />
              處理步驟
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <div className={`w-3 h-3 rounded-full ${getStageProgress() > 10 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={`${getStageProgress() > 10 ? 'text-gray-800' : 'text-gray-500'}`}>分析原始題目結構</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className={`w-3 h-3 rounded-full ${getStageProgress() > 25 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={`${getStageProgress() > 25 ? 'text-gray-800' : 'text-gray-500'}`}>生成相似題目模式</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className={`w-3 h-3 rounded-full ${getStageProgress() > 50 ? 'bg-green-500' : getStageProgress() > 25 ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                <span className={`${getStageProgress() > 25 ? 'text-gray-800' : 'text-gray-500'}`}>創建 5 道練習題</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className={`w-3 h-3 rounded-full ${getStageProgress() > 75 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={`${getStageProgress() > 75 ? 'text-gray-800' : 'text-gray-500'}`}>生成詳細解答</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className={`w-3 h-3 rounded-full ${getStageProgress() > 90 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={`${getStageProgress() > 90 ? 'text-gray-800' : 'text-gray-500'}`}>驗證題目完整性</span>
              </div>
            </div>
          </div>

          {/* Estimated Time */}
          <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <p className="text-sm text-gray-700">
              預計完成時間: <span className="font-semibold text-blue-600">10-15 秒</span>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              請耐心等待，AI 正在為您生成高質量的練習題
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrillProgress; 